/**
 * First-login product tour — Apple-style.
 *
 * Each step renders a self-contained mock scene (a faux dashboard, a faux
 * chat panel, faux slides…) with a single highlighted region and a one-line
 * caption. No driver.js, no external lib — the scenes are inline JSX so we
 * have full control over typography, spacing, and the spotlight ring.
 *
 * Persistence: localStorage `pp_onboarding_seen_v1`. A `pp:onboarding-replay`
 * window event re-runs the tour without clearing the flag (hook for a future
 * Help menu entry).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus, Sparkle, FileText, ArrowRight, ArrowsOut,
  DownloadSimple, ChatCircleText, Check,
} from '@phosphor-icons/react';

const STORAGE_KEY = 'pp_onboarding_seen_v1';
const REPLAY_EVENT = 'pp:onboarding-replay';
const FORCE_KEY = 'pp_tour_force_v1';

// Module-load: capture ?tour=1 *before* the app's router can strip it.
// (The login flow redirects via `window.location.pathname` which drops the
//  query string — so by the time OnboardingTour mounts after auth, the URL
//  is already clean. Stash the force flag in sessionStorage so it survives.)
if (typeof window !== 'undefined') {
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.get('tour') === '1') {
      sessionStorage.setItem(FORCE_KEY, '1');
      u.searchParams.delete('tour');
      window.history.replaceState({}, '', u.toString());
    }
  } catch {}
}

interface OnboardingTourProps {
  enabled: boolean;
}

interface Step {
  /** Section title shown above the mock scene */
  eyebrow: string;
  /** Big single-sentence headline (Apple style) */
  headline: string;
  /** Optional subheadline (kept short) */
  sub?: string;
  /** The mock scene component */
  Scene: React.FC;
}

export default function OnboardingTour({ enabled }: OnboardingTourProps) {
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  const close = useCallback((markSeen: boolean) => {
    setOpen(false);
    if (markSeen) {
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    }
  }, []);

  // First-login detection
  useEffect(() => {
    if (!enabled) return;
    // ?tour=1 was already captured at module load (see top of file) and
    // stashed in sessionStorage so it survives the login redirect that
    // strips query strings.
    let force = false;
    try {
      if (sessionStorage.getItem(FORCE_KEY) === '1') {
        force = true;
        sessionStorage.removeItem(FORCE_KEY);
      }
    } catch {}
    let seen = '';
    try { seen = localStorage.getItem(STORAGE_KEY) || ''; } catch {}
    if (force || !seen) {
      setStepIdx(0);
      setOpen(true);
    }
    const onReplay = () => { setStepIdx(0); setOpen(true); };
    window.addEventListener(REPLAY_EVENT, onReplay);
    return () => window.removeEventListener(REPLAY_EVENT, onReplay);
  }, [enabled]);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(true);
      else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (stepIdx < STEPS.length - 1) setStepIdx(i => i + 1);
        else close(true);
      } else if (e.key === 'ArrowLeft') {
        if (stepIdx > 0) setStepIdx(i => i - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, stepIdx, close]);

  if (!open) return null;

  const step = STEPS[stepIdx];
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === STEPS.length - 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome tour"
      style={{
        position: 'fixed', inset: 0, zIndex: 700,
        background: 'rgba(15, 18, 24, 0.78)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex', flexDirection: 'column',
        animation: 'pp-tour-fade 0.25s ease',
      }}
    >
      {/* Top-right Skip — quiet, always available */}
      <button
        onClick={() => close(true)}
        style={{
          position: 'absolute', top: 24, right: 28,
          padding: '6px 14px', borderRadius: 8,
          background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
          color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
          fontSize: 'var(--fs-xs)', fontWeight: 400, letterSpacing: '0.04em',
          fontFamily: 'inherit', transition: 'all 0.15s ease',
        }}
        onMouseOver={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.95)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
        onMouseOut={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
      >
        Skip tour
      </button>

      {/* Stage area — center the mock + caption */}
      <div style={{
        flex: 1,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '64px 32px 32px',
        overflow: 'hidden',
      }}>
        {/* Eyebrow + headline (Apple-style, big, light) */}
        <div style={{ textAlign: 'center', marginBottom: 36, maxWidth: 720 }}>
          <div style={{
            fontSize: 'var(--fs-xs)', fontWeight: 500,
            color: 'rgba(255,255,255,0.5)',
            letterSpacing: '0.16em', textTransform: 'uppercase',
            marginBottom: 14,
          }}>
            {step.eyebrow}
          </div>
          <h1 style={{
            fontSize: 'clamp(28px, 3.6vw, 44px)',
            fontWeight: 300,
            lineHeight: 1.18,
            color: '#fff',
            margin: 0,
            letterSpacing: '-0.01em',
          }}>
            {step.headline}
          </h1>
          {step.sub && (
            <p style={{
              fontSize: 'clamp(14px, 1.3vw, 17px)',
              fontWeight: 300, lineHeight: 1.55,
              color: 'rgba(255,255,255,0.65)',
              margin: '14px auto 0', maxWidth: 540,
            }}>
              {step.sub}
            </p>
          )}
        </div>

        {/* Mock scene canvas */}
        <div
          key={stepIdx}
          style={{
            width: 'min(960px, 90vw)',
            height: 'min(440px, 50vh)',
            background: 'var(--bg-surface)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 16,
            boxShadow: '0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)',
            overflow: 'hidden',
            position: 'relative',
            animation: 'pp-tour-scene-in 0.35s cubic-bezier(0.2, 0.8, 0.2, 1)',
          }}
        >
          <step.Scene />
        </div>
      </div>

      {/* Bottom controls — step dots + nav */}
      <div style={{
        padding: '20px 32px 36px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 24,
      }}>
        <button
          onClick={() => setStepIdx(i => Math.max(0, i - 1))}
          disabled={isFirst}
          style={{
            padding: '10px 20px', borderRadius: 10,
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.15)',
            color: isFirst ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.85)',
            cursor: isFirst ? 'default' : 'pointer',
            fontSize: 'var(--fs-sm)', fontWeight: 400,
            fontFamily: 'inherit', letterSpacing: '0.02em',
            transition: 'all 0.15s ease',
            opacity: isFirst ? 0.4 : 1,
          }}
        >
          Back
        </button>

        {/* Step dots */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStepIdx(i)}
              aria-label={`Step ${i + 1}`}
              style={{
                width: i === stepIdx ? 22 : 6, height: 6,
                borderRadius: 3,
                background: i === stepIdx ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.25)',
                border: 'none', cursor: 'pointer',
                transition: 'all 0.25s ease',
                padding: 0,
              }}
            />
          ))}
        </div>

        <button
          onClick={() => isLast ? close(true) : setStepIdx(i => i + 1)}
          style={{
            padding: '10px 26px', borderRadius: 10,
            background: '#fff',
            border: 'none',
            color: '#0e1116',
            cursor: 'pointer',
            fontSize: 'var(--fs-sm)', fontWeight: 500,
            fontFamily: 'inherit', letterSpacing: '0.02em',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            transition: 'opacity 0.15s ease, transform 0.1s ease',
          }}
          onMouseOver={e => { e.currentTarget.style.opacity = '0.92'; }}
          onMouseOut={e => { e.currentTarget.style.opacity = '1'; }}
          onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px)'; }}
          onMouseUp={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          {isLast ? 'Get started' : 'Continue'}
          {!isLast && <ArrowRight size={14} weight="bold" />}
        </button>
      </div>

      <style>{`
        @keyframes pp-tour-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes pp-tour-scene-in {
          from { opacity: 0; transform: translateY(12px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes pp-tour-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(80,140,255,0.45); }
          70%  { box-shadow: 0 0 0 14px rgba(80,140,255,0); }
          100% { box-shadow: 0 0 0 0   rgba(80,140,255,0); }
        }
        @keyframes pp-tour-cursor {
          0%, 49%   { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Shared mock-UI helpers
// ────────────────────────────────────────────────────────────────────

const MOCK_SURFACE: React.CSSProperties = {
  width: '100%', height: '100%',
  background: 'var(--bg-primary)',
  display: 'flex', flexDirection: 'column',
};

const MOCK_TOPBAR: React.CSSProperties = {
  height: 36, flexShrink: 0,
  display: 'flex', alignItems: 'center',
  padding: '0 14px', gap: 6,
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg-surface)',
};

const dot = (color: string): React.CSSProperties => ({
  width: 10, height: 10, borderRadius: '50%', background: color,
});

function MockTopbar() {
  return (
    <div style={MOCK_TOPBAR}>
      <div style={dot('#ff5f57')} />
      <div style={dot('#febc2e')} />
      <div style={dot('#28c840')} />
      <div style={{
        marginLeft: 16, fontSize: 'var(--fs-2xs)',
        color: 'var(--text-muted)', letterSpacing: '0.04em',
      }}>
        pitchpilot.app
      </div>
    </div>
  );
}

/** A spotlight ring around a child. Pulses to draw attention. */
function Spotlight({
  children, padding = 4, radius = 12,
  style,
}: {
  children: React.ReactNode;
  padding?: number;
  radius?: number;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        padding,
        borderRadius: radius,
        animation: 'pp-tour-pulse 2.2s ease-out infinite',
        boxShadow: '0 0 0 2px var(--brand)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Scenes
// ────────────────────────────────────────────────────────────────────

function SceneWelcome() {
  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 24,
      background: 'linear-gradient(180deg, var(--bg-surface), var(--bg-primary))',
    }}>
      <div style={{
        width: 96, height: 96, borderRadius: '50%',
        border: '1px solid var(--border-strong)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-secondary)',
        fontSize: 24, fontWeight: 200, letterSpacing: '0.05em',
      }}>
        PP
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        color: 'var(--text-muted)', fontSize: 'var(--fs-sm)',
        fontWeight: 300,
      }}>
        <Sparkle size={14} weight="thin" />
        <span>An AI agent that knows your Confluence</span>
        <Sparkle size={14} weight="thin" />
      </div>
    </div>
  );
}

function SceneDashboard() {
  return (
    <div style={MOCK_SURFACE}>
      <MockTopbar />
      <div style={{
        flex: 1, display: 'grid',
        gridTemplateColumns: '180px 1fr',
        minHeight: 0,
      }}>
        {/* Sidebar */}
        <div style={{
          background: 'var(--bg-sidebar)',
          borderRight: '1px solid var(--border)',
          padding: '14px 12px',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <div style={{
            fontSize: 'var(--fs-2xs)', color: 'var(--sidebar-text-muted)',
            letterSpacing: '0.08em', marginBottom: 6, textTransform: 'uppercase',
          }}>Workspaces</div>
          {['Acme', 'Personal', 'Marketing'].map((w, i) => (
            <div key={w} style={{
              padding: '6px 10px', borderRadius: 6,
              fontSize: 'var(--fs-sm)', fontWeight: i === 0 ? 500 : 300,
              color: i === 0 ? 'var(--sidebar-text)' : 'var(--sidebar-text-sub)',
              background: i === 0 ? 'rgba(255,255,255,0.04)' : 'transparent',
            }}>{w}</div>
          ))}
        </div>

        {/* Main */}
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 18, padding: '32px 40px',
        }}>
          <div style={{
            fontSize: 'var(--fs-md)', fontWeight: 300,
            color: 'var(--text-primary)', letterSpacing: '-0.005em',
          }}>
            Your decks live here.
          </div>
          <div style={{ width: 32, height: 1, background: 'var(--border)' }} />
          <Spotlight padding={2} radius={6}>
            <button style={{
              padding: '8px 22px',
              fontSize: 'var(--fs-xs)', fontWeight: 400,
              color: 'var(--text-secondary)',
              background: 'none',
              border: '1px solid var(--border)',
              borderRadius: 4,
              fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              letterSpacing: '0.02em',
            }}>
              <Plus size={11} weight="light" />
              New Presentation
            </button>
          </Spotlight>
        </div>
      </div>
    </div>
  );
}

function SceneChat() {
  // Typed-out animation for the prompt
  const fullText = 'Build a 7-slide deck for Acme’s Q1 kickoff. Use the roadmap and the new pricing memo from Confluence.';
  const [typed, setTyped] = useState('');
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i++;
      setTyped(fullText.slice(0, i));
      if (i >= fullText.length) clearInterval(id);
    }, 26);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={MOCK_SURFACE}>
      <MockTopbar />
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 0 }}>
        {/* Chat column */}
        <div style={{
          borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column',
          background: 'var(--bg-chat)',
        }}>
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid var(--border)',
            fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', letterSpacing: '0.06em',
          }}>
            CHAT
          </div>
          <div style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{
              alignSelf: 'flex-start',
              padding: '10px 14px', borderRadius: 10,
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 300,
              maxWidth: '80%',
            }}>
              <ChatCircleText size={11} weight="thin" style={{ marginRight: 6, verticalAlign: 'middle' }} />
              How can I help?
            </div>
          </div>
          <div style={{ padding: '12px' }}>
            <Spotlight padding={2} radius={10} style={{ display: 'flex', width: '100%' }}>
              <div style={{
                width: '100%',
                padding: '10px 14px',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-primary)',
                lineHeight: 1.5, minHeight: 64,
              }}>
                {typed}
                <span style={{
                  display: 'inline-block', width: 1, height: 12,
                  background: 'var(--text-primary)', marginLeft: 1,
                  verticalAlign: 'middle',
                  animation: 'pp-tour-cursor 1s steps(2, end) infinite',
                }} />
              </div>
            </Spotlight>
          </div>
        </div>
        {/* Preview placeholder */}
        <div style={{
          background: 'var(--bg-preview)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', fontWeight: 300,
        }}>
          Slides appear here as the agent works.
        </div>
      </div>
    </div>
  );
}

function SceneConfluence() {
  const pages = [
    { title: 'Q1 Product Roadmap', selected: true },
    { title: 'Pricing Memo · 2026 H1', selected: true },
    { title: 'Customer Acme · Account Plan', selected: false },
    { title: 'Engineering OKRs', selected: false },
  ];
  return (
    <div style={MOCK_SURFACE}>
      <MockTopbar />
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}>
        <div style={{
          width: 460, maxWidth: '90%',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)', borderRadius: 12,
          boxShadow: '0 8px 40px rgba(0,0,0,0.16)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '14px 20px', borderBottom: '1px solid var(--border)',
            fontSize: 'var(--fs-md)', fontWeight: 400, color: 'var(--text-primary)',
            letterSpacing: '0.01em',
          }}>
            Acme · Confluence
          </div>
          <div>
            {pages.map((p, i) => {
              const row = (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 20px',
                  borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  fontSize: 'var(--fs-sm)', fontWeight: 300,
                  color: p.selected ? 'var(--text-primary)' : 'var(--text-secondary)',
                  background: 'transparent',
                }}>
                  <span style={{
                    width: 14, height: 14, borderRadius: 3,
                    border: `1px solid ${p.selected ? 'var(--brand)' : 'var(--border-strong)'}`,
                    background: p.selected ? 'var(--brand)' : 'transparent',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {p.selected && <Check size={9} weight="bold" style={{ color: '#fff' }} />}
                  </span>
                  <FileText size={13} weight="light" style={{ color: 'var(--text-muted)' }} />
                  <span>{p.title}</span>
                </div>
              );
              return p.selected && i === 0
                ? <Spotlight key={p.title} padding={0} radius={0} style={{ display: 'block', boxShadow: 'inset 3px 0 0 var(--brand)' }}>{row}</Spotlight>
                : <React.Fragment key={p.title}>{row}</React.Fragment>;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function SceneStoryboard() {
  return (
    <div style={MOCK_SURFACE}>
      <MockTopbar />
      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 24, padding: '8px 24px',
        borderBottom: '1px solid var(--border)',
        fontSize: 'var(--fs-sm)',
      }}>
        {['Brief', 'Storyboard', 'Notes', 'Presentation'].map((t, i) => (
          <div key={t} style={{
            padding: '4px 0',
            color: i === 1 ? 'var(--text-primary)' : 'var(--text-muted)',
            fontWeight: i === 1 ? 500 : 300,
            borderBottom: i === 1 ? '2px solid var(--text-primary)' : '2px solid transparent',
          }}>{t}</div>
        ))}
      </div>
      <div style={{
        flex: 1, padding: 28, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        fontSize: 'var(--fs-xs)', lineHeight: 1.7,
        color: 'var(--text-secondary)',
        overflow: 'hidden',
      }}>
        <Spotlight padding={10} radius={10} style={{ display: 'block', maxWidth: 540 }}>
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '14px 16px',
          }}>
            <div style={{ color: 'var(--text-muted)', marginBottom: 8 }}>
              ## Slide 3 — The opportunity
            </div>
            <div>· Total addressable market · $4.2B</div>
            <div>· Acme’s slice · ~$280M ARR</div>
            <div>· Why now · regulator deadline Q3</div>
            <div style={{ color: 'var(--text-muted)', marginTop: 10, fontWeight: 400 }}>
              source: Q1 Roadmap p.7
            </div>
          </div>
        </Spotlight>
      </div>
    </div>
  );
}

function ScenePresentation() {
  const slides = [
    { num: '1', title: 'Cover', active: false },
    { num: '2', title: 'Why now', active: false },
    { num: '3', title: 'Opportunity', active: true },
    { num: '4', title: 'The plan', active: false },
    { num: '5', title: 'Next steps', active: false },
  ];
  return (
    <div style={MOCK_SURFACE}>
      <MockTopbar />
      <div style={{
        display: 'flex', gap: 24, padding: '8px 24px',
        borderBottom: '1px solid var(--border)',
        fontSize: 'var(--fs-sm)',
      }}>
        {['Brief', 'Storyboard', 'Notes', 'Presentation'].map((t, i) => (
          <div key={t} style={{
            padding: '4px 0',
            color: i === 3 ? 'var(--text-primary)' : 'var(--text-muted)',
            fontWeight: i === 3 ? 500 : 300,
            borderBottom: i === 3 ? '2px solid var(--text-primary)' : '2px solid transparent',
          }}>{t}</div>
        ))}
      </div>
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        padding: 24, gap: 14, minHeight: 0,
      }}>
        {/* Big slide */}
        <div style={{
          flex: 1, minHeight: 0,
          background: 'linear-gradient(135deg, #f6f7fb, #eef0f6)',
          border: '1px solid var(--border)', borderRadius: 10,
          display: 'flex', flexDirection: 'column',
          padding: '28px 36px', gap: 12,
          color: '#1e293b',
        }}>
          <div style={{ fontSize: 'var(--fs-xs)', color: '#64748b', letterSpacing: '0.06em' }}>03</div>
          <div style={{ fontSize: 'clamp(20px,2.4vw,28px)', fontWeight: 400, letterSpacing: '-0.01em' }}>
            The opportunity
          </div>
          <div style={{ height: 1, background: '#cbd5e1', width: 60, margin: '6px 0' }} />
          <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 300, lineHeight: 1.6 }}>
            $4.2B market · $280M Acme ARR · Q3 regulator deadline.
          </div>
        </div>
        {/* Thumb strip */}
        <div style={{ display: 'flex', gap: 8 }}>
          {slides.map(s => {
            const thumb = (
              <div style={{
                flex: 1, padding: '6px 8px',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 6, textAlign: 'center',
                fontSize: 'var(--fs-2xs)',
              }}>
                <div style={{ color: 'var(--text-muted)' }}>{s.num}</div>
                <div style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</div>
              </div>
            );
            return s.active
              ? <Spotlight key={s.num} padding={2} radius={8} style={{ flex: 1, display: 'flex' }}>{thumb}</Spotlight>
              : <div key={s.num} style={{ flex: 1, display: 'flex' }}>{thumb}</div>;
          })}
        </div>
      </div>
    </div>
  );
}

function SceneToolbar() {
  const buttons = [
    { icon: <FileText size={12} weight="light" />, label: 'Preview' },
    { icon: <DownloadSimple size={12} weight="light" />, label: 'HTML' },
    { icon: <ArrowsOut size={12} weight="light" />, label: 'Fullscreen' },
  ];
  // Sequence: each button glows in turn
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActiveIdx(i => (i + 1) % buttons.length), 900);
    return () => clearInterval(id);
  }, [buttons.length]);

  return (
    <div style={MOCK_SURFACE}>
      <MockTopbar />
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 24px', borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', gap: 24, fontSize: 'var(--fs-sm)' }}>
          {['Brief', 'Storyboard', 'Notes', 'Presentation'].map((t, i) => (
            <div key={t} style={{
              padding: '4px 0',
              color: i === 3 ? 'var(--text-primary)' : 'var(--text-muted)',
              fontWeight: i === 3 ? 500 : 300,
              borderBottom: i === 3 ? '2px solid var(--text-primary)' : '2px solid transparent',
            }}>{t}</div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {buttons.map((b, i) => {
            const pill = (
              <div style={{
                padding: '4px 10px',
                fontSize: 'var(--fs-2xs)',
                color: 'var(--text-secondary)',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 6,
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontWeight: 400, letterSpacing: '0.02em',
              }}>
                {b.icon}
                <span>{b.label}</span>
              </div>
            );
            return i === activeIdx
              ? <Spotlight key={b.label} padding={2} radius={8}>{pill}</Spotlight>
              : <React.Fragment key={b.label}>{pill}</React.Fragment>;
          })}
        </div>
      </div>
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        background: 'linear-gradient(135deg, #f6f7fb, #eef0f6)',
        color: '#475569', fontSize: 'var(--fs-sm)', fontWeight: 300,
      }}>
        Your slide preview.
      </div>
    </div>
  );
}

function SceneFinish() {
  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 20,
      background: 'linear-gradient(180deg, var(--bg-surface), var(--bg-primary))',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        background: 'var(--brand)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff',
      }}>
        <Check size={28} weight="thin" />
      </div>
      <div style={{
        fontSize: 'var(--fs-md)', fontWeight: 300,
        color: 'var(--text-primary)', letterSpacing: '0.01em',
      }}>
        That's the whole flow.
      </div>
      <div style={{
        fontSize: 'var(--fs-sm)', fontWeight: 300,
        color: 'var(--text-muted)', textAlign: 'center', maxWidth: 360,
        lineHeight: 1.6,
      }}>
        Click <strong style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>Get started</strong> to create your first deck.
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Step list — order matters
// ────────────────────────────────────────────────────────────────────

const STEPS: Step[] = [
  {
    eyebrow: 'Welcome',
    headline: 'Pitch decks, drafted by an agent that reads your wiki.',
    sub: 'A 90-second tour. Skip whenever you want.',
    Scene: SceneWelcome,
  },
  {
    eyebrow: 'Step 1',
    headline: 'Start a new deck.',
    sub: 'One click. Each deck has its own chat and slide preview.',
    Scene: SceneDashboard,
  },
  {
    eyebrow: 'Step 2',
    headline: 'Tell the agent what you need.',
    sub: 'Free-form prompt. The agent plans the storyboard and asks follow-ups if anything is missing.',
    Scene: SceneChat,
  },
  {
    eyebrow: 'Step 3',
    headline: 'Pull from your Confluence.',
    sub: 'Reference any page from chat. The agent quotes the source instead of making things up.',
    Scene: SceneConfluence,
  },
  {
    eyebrow: 'Step 4',
    headline: 'A storyboard you can edit.',
    sub: 'The agent drafts the outline, slide by slide. Change anything — it re-renders on save.',
    Scene: SceneStoryboard,
  },
  {
    eyebrow: 'Step 5',
    headline: 'Polished slides, ready to present.',
    sub: 'Click a thumb to jump. Arrow keys advance.',
    Scene: ScenePresentation,
  },
  {
    eyebrow: 'Step 6',
    headline: 'Preview and download the result.',
    sub: 'The current Mosoo showcase previews HTML artifacts in the app and downloads the same HTML file.',
    Scene: SceneToolbar,
  },
  {
    eyebrow: 'You’re ready',
    headline: 'Build your first deck.',
    sub: 'Re-open this tour any time from the workspace menu.',
    Scene: SceneFinish,
  },
];
